import { useEffect, useState, useCallback } from "react";
import { createUseStyles } from "react-jss";
import clsx from "clsx";
import isEqual from "lodash/isEqual";

import { useWithSharedEvent, onSharedEvent } from "../utils/useWithSharedEvent";
import { getTheme } from "../Customisation";

const useStyles = createUseStyles(theme => ({
  content: {
    background: theme.popupSecondary,
    color: "white",
    "box-sizing": "border-box",
    position: "relative",
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
    height: "calc(100vh - 43px)",
    width: "100%",
    marginTop: getTheme().popupSpace ?? 1,
  },
  image: {
    width: "100%",
    height: "100%",
    flexShrink: 0,
    backgroundSize: "cover",
    "background-position": "center",
    transition: "all 0.5s ease",
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",

    "& img": {
      maxHeight: "100%",
      maxWidth: "100%",
    },
  },
  button: {
    position: "absolute",
    height: 60,
    width: 30,
    top: "calc(50% - 30px)",
    background: theme.popupSecondary,
    cursor: "pointer",
    zIndex: 10,
    transition: "background-color 0.2s ease",
    fontSize: 25,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "&:hover": {
      background: theme.popupPrimary,
    },
    "&::selection": {
      background: "none",
    },
  },
  left: {
    left: 0,
    borderRadius: [0, 3, 3, 0],
  },
  right: {
    right: 0,
    borderRadius: [3, 0, 0, 3],
  },
}));

const Gallery = ({ config }) => {
  const classes = useStyles();
  const [count, setCount] = useState(0);
  const [images, setImages] = useState([]);
  const [loaded, setLoaded] = useState(0);

  useEffect(() => {
    setLoaded(l => (count >= l ? count : l));
  }, [count]);

  useEffect(() => {
    const url = "https://manage.theatro360.com/api/v2/gallery";
    if (config.options.gallery) {
      fetch(`${url}/${config.options.gallery.id}`)
        .then(res => res.json())
        .then(data =>
          setImages(
            data.entries.map(({ id, public_url, caption }) => ({
              id,
              url: `${public_url}?auto=format&q=60&max-w=${window.innerWidth}&fit=fill`,
              caption,
            })),
          ),
        )
        .catch(err => console.error(err));
    } else {
      setImages(
        (config.options.content ?? []).map(url => ({
          url,
          id: url,
          caption: null,
        })),
      );
    }
  }, [config.options.content, config.options.gallery]);

  // Shared tour code
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const galleryShareState = useCallback(
    useWithSharedEvent(data => {
      // no op here, the idea is just passing "data" to the other side
      // which is handled automatically by useWithSharedEvent
    }, "GALLERY_POPUP_SHARE_STATE_" + config.options.gallery?.id),
    [config.options.gallery],
  );

  useEffect(() => {
    galleryShareState({ count, images });
  }, [count, images, galleryShareState]);

  useEffect(() => {
    onSharedEvent(
      "GALLERY_POPUP_SHARE_STATE_" + config.options.gallery?.id,
      payload => {
        if (!isEqual(count, payload.count)) {
          setCount(payload.count);
        }
        if (!isEqual(images, payload.images)) {
          setCount(payload.images);
        }
      },
    );
  }, [count, setCount, images, setImages, config.options.gallery]);

  return (
    <div className={classes.content} onClick={evt => evt.stopPropagation()}>
      {images.length > 1 && (
        <>
          <div
            className={clsx(classes.button, classes.left)}
            onClick={() => setCount(c => (c === 0 ? images.length - 1 : c - 1))}
          >
            ❮
          </div>
          <div
            className={clsx(classes.button, classes.right)}
            onClick={() => setCount(c => (c === images.length - 1 ? 0 : c + 1))}
          >
            ❯
          </div>
        </>
      )}
      {images.slice(0, loaded + 2).map(({ id, url, caption = null }) =>
        config.options.fit === "fit" ? (
          <div
            key={id}
            className={classes.image}
            style={{
              left: `-${100 * count}%`,
            }}
          >
            <img src={url} alt={caption} />
          </div>
        ) : (
          <div
            key={id}
            className={classes.image}
            style={{
              left: `-${100 * count}%`,
              backgroundImage: `url("${url}")`,
            }}
          />
        ),
      )}
    </div>
  );
};

export default Gallery;
